/**
 * Banner Carrousel
 * Componente de carrossel usando BlazeSlider
 */

class BannerCarrousel extends HTMLElement {
  constructor() {
    super();
    
    this.slider = this.querySelector('.blaze-slider');
    this.videos = this.querySelectorAll('video');
    this.playBtns = this.querySelectorAll('.banner__video-play');
    this.sliderElement = null;
    this.maxRetries = 10;
    this.retryDelay = 100;
    this.retryCount = 0;
    this.autoplayInterval = 5000;
    this.progressBars = [];
    this.currentProgressBar = null;
    this.currentSlideIndex = 0;
    this.progressAnimation = null;
    this.isManualInteraction = false;
    this.touchStartX = null;
    this.touchStartY = null;
    this.autoplayTimer = null;
    this.useManualAutoplay = true; // Control autoplay manually for better restart control
    this.initialized = false;
    this.initializationPromise = null;
    this.autoplayStartDelay = 20000;
    this.initialSlideLockUntil = 0;
  }

  hydrateImage(image) {
    if (!image || !image.dataset.srcset) return;
    image.srcset = image.dataset.srcset;
    if (image.dataset.src) image.src = image.dataset.src;
    image.removeAttribute('data-srcset');
    image.removeAttribute('data-src');
  }

  hydrateDeferredImages() {
    this.querySelectorAll('img[data-srcset]').forEach((image) => this.hydrateImage(image));
  }

  prepareSlideImage(index) {
    const image = this.querySelectorAll('.blaze-slide img')[index];
    this.hydrateImage(image);
  }

  connectedCallback() {
    this.slider = this.querySelector('.blaze-slider');
    this.videos = this.querySelectorAll('video');
    this.playBtns = this.querySelectorAll('.banner__video-play');

    const hydrate = () => this.hydrateDeferredImages();
    const initialize = () => this.initializeBlazer();
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(initialize, { timeout: 3000 });
    } else {
      window.setTimeout(initialize, 3000);
    }
    ['pointerdown', 'keydown', 'touchstart'].forEach((eventName) => {
      this.addEventListener(eventName, () => {
        hydrate();
        initialize();
      }, { once: true, passive: true });
    });
  }

  /**
   * Wait for BlazeSlider to be available with retry mechanism
   */
  waitForBlazeSlider() {
    return new Promise((resolve, reject) => {
      if (typeof BlazeSlider !== 'undefined') {
        resolve();
        return;
      }

      const checkInterval = setInterval(() => {
        this.retryCount++;
        
        if (typeof BlazeSlider !== 'undefined') {
          clearInterval(checkInterval);
          resolve();
        } else if (this.retryCount >= this.maxRetries) {
          clearInterval(checkInterval);
          reject(new Error('BlazeSlider failed to load after maximum retries'));
        }
      }, this.retryDelay);
    });
  }

  async initializeBlazer() {
    if (this.initialized) return;
    if (this.initializationPromise) return this.initializationPromise;

    this.initializationPromise = this.initializeBlazerInternal();
    await this.initializationPromise;
    this.initializationPromise = null;
  }

  async initializeBlazerInternal() {
    if (!this.slider) {
      console.warn('BannerCarrousel: Slider element not found');
      return;
    }

    const slides = this.querySelectorAll('.blaze-slide');
    const slidesCount = slides ? slides.length : 0;

    if (slidesCount <= 1) {
      const prevBtn = this.querySelector('.blaze-prev');
      const nextBtn = this.querySelector('.blaze-next');
      const pagination = this.querySelector('.blaze-pagination-banner-carrousel');
      
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
      if (pagination) pagination.style.display = 'none';
      return;
    }

    try {
      // Wait for BlazeSlider to be available
      await this.waitForBlazeSlider();

      // Initialize BlazeSlider with error handling
      try {
        // Disable BlazeSlider's built-in autoplay if we're using manual control
        this.sliderElement = new BlazeSlider(this.slider, {
          all: {
            transitionDuration: 300,
            slidesToShow: 1,
            slideGap: '0px',
            // loop:false e obrigatorio aqui. Com o loop ligado o BlazeSlider
            // reordena os slides no DOM, e o slide que o Liquid marcou como
            // primeiro (unico com loading=eager, fetchpriority=high e o
            // <link rel=preload>) ia parar fora da tela. O navegador ficava
            // priorizando uma imagem invisivel e tratando como preguicosa
            // justamente a que precisava ser pintada: LCP de 11s e nota 60.
            // A volta do ultimo slide para o primeiro e feita em avancarSlide().
            loop: false,
            enableAutoplay: !this.useManualAutoplay, // Disable if using manual control
            autoplayInterval: this.autoplayInterval,
            stopAutoplayOnInteraction: false,
          }
        });

        this.initialSlideLockUntil = performance.now() + this.autoplayStartDelay;
        const track = this.querySelector('.blaze-track');
        if (track) {
          track.style.transform = 'translate3d(0px, 0px, 0px)';
        }

        // Start manual autoplay if enabled
        if (this.useManualAutoplay) this.scheduleManualAutoplay();

        // Initialize progress bars
        this.initializeProgressBars();

        // Setup manual interaction detection (must be after initializeProgressBars)
        this.setupManualInteractionDetection();

        // Handle slide change to remove first-banner class and update progress
        this.sliderElement.onSlide((pageIndex) => {
          if (
            !this.isManualInteraction &&
            performance.now() < this.initialSlideLockUntil &&
            pageIndex !== 0
          ) {
            return;
          }

          const animatedEntry = this.querySelector('.first-banner');
          if (animatedEntry) {
            animatedEntry.classList.remove('first-banner');
          }
          
          // Update progress bar with the correct slide index
          if (pageIndex !== undefined && pageIndex !== null) {
            this.currentSlideIndex = pageIndex;
            
            // If it was manual interaction, restart autoplay and progress bar
            if (this.isManualInteraction) {
              this.restartAutoplayAndProgress();
              this.isManualInteraction = false;
            } else {
              this.updateProgressBar();
            }
          }
        });

        console.log('BannerCarrousel: Successfully initialized');
        this.initialized = true;
      } catch (initError) {
        console.error('BannerCarrousel: Failed to initialize BlazeSlider', initError);
        this.showFallbackUI();
      }
    } catch (loadError) {
      console.error('BannerCarrousel: BlazeSlider library not available', loadError);
      this.showFallbackUI();
    }
  }

  /**
   * Setup manual interaction detection (swipe, drag, button clicks)
   */
  setupManualInteractionDetection() {
    const track = this.querySelector('.blaze-track');
    const prevBtn = this.querySelector('.blaze-prev');
    const nextBtn = this.querySelector('.blaze-next');
    
    if (!track) return;

    // Detect touch/swipe
    track.addEventListener('touchstart', (e) => {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
    }, { passive: true });

    track.addEventListener('touchend', (e) => {
      if (this.touchStartX !== null && this.touchStartY !== null) {
        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;
        const diffX = Math.abs(touchEndX - this.touchStartX);
        const diffY = Math.abs(touchEndY - this.touchStartY);
        
        // If horizontal swipe is greater than vertical, it's a swipe
        if (diffX > diffY && diffX > 10) {
          this.isManualInteraction = true;
          // Immediately restart autoplay when swipe detected
          if (this.useManualAutoplay) {
            this.startManualAutoplay();
          }
        }
        
        this.touchStartX = null;
        this.touchStartY = null;
      }
    }, { passive: true });

    // Detect mouse drag
    let isDragging = false;
    let dragStartX = null;

    track.addEventListener('mousedown', (e) => {
      isDragging = true;
      dragStartX = e.clientX;
    });

    track.addEventListener('mousemove', (e) => {
      if (isDragging && dragStartX !== null) {
        const diffX = Math.abs(e.clientX - dragStartX);
        if (diffX > 10) {
          this.isManualInteraction = true;
          // Immediately restart autoplay when drag detected
          if (this.useManualAutoplay) {
            this.startManualAutoplay();
          }
        }
      }
    });

    track.addEventListener('mouseup', () => {
      isDragging = false;
      dragStartX = null;
    });

    track.addEventListener('mouseleave', () => {
      isDragging = false;
      dragStartX = null;
    });

    // Detect button clicks
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        this.isManualInteraction = true;
        // Immediately restart autoplay when button clicked
        if (this.useManualAutoplay) {
          this.startManualAutoplay();
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        this.isManualInteraction = true;
        // Immediately restart autoplay when button clicked
        if (this.useManualAutoplay) {
          this.startManualAutoplay();
        }
      });
    }

    // Detect progress bar clicks (bars are already initialized at this point)
    if (this.progressBars && this.progressBars.length > 0) {
      this.progressBars.forEach((bar) => {
        bar.addEventListener('click', () => {
          this.isManualInteraction = true;
          // Immediately restart autoplay when bar clicked
          if (this.useManualAutoplay) {
            this.startManualAutoplay();
          }
        });
      });
    }
  }

  /**
   * Start manual autoplay control
   */
  startManualAutoplay() {
    this.stopManualAutoplay();
    
    this.autoplayTimer = setInterval(() => {
      if (this.sliderElement && typeof this.sliderElement.next === 'function') {
        const nextIndex = (this.currentSlideIndex + 1) % this.querySelectorAll('.blaze-slide').length;
        this.prepareSlideImage(nextIndex);
        this.avancarSlide();
      }
    }, this.autoplayInterval);
  }

  /**
   * Avanca um slide. Como loop:false, ao chegar no ultimo volta ao primeiro
   * manualmente para o carrossel seguir girando sem fim.
   */
  avancarSlide() {
    const slider = this.sliderElement;
    if (!slider) return;
    const total = this.querySelectorAll('.blaze-slide').length;
    if (total < 2) return;

    if (this.currentSlideIndex >= total - 1) {
      if (typeof slider.shuffle === 'function') {
        slider.shuffle(total - 1, false);
      } else if (typeof slider.prev === 'function') {
        for (let i = 0; i < total - 1; i++) slider.prev();
      }
      this.currentSlideIndex = 0;
    } else if (typeof slider.next === 'function') {
      slider.next();
    }
  }

  scheduleManualAutoplay() {
    this.stopManualAutoplay();
    this.autoplayTimer = setTimeout(() => {
      const nextIndex = (this.currentSlideIndex + 1) % this.querySelectorAll('.blaze-slide').length;
      this.prepareSlideImage(nextIndex);
      this.avancarSlide();
      this.startManualAutoplay();
      this.startProgressBar();
    }, this.autoplayStartDelay);
  }

  /**
   * Stop manual autoplay control
   */
  stopManualAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  /**
   * Restart autoplay and progress bar after manual interaction
   */
  restartAutoplayAndProgress() {
    // Stop current progress bar
    this.stopProgressBar();

    // Reset current progress bar to 0%
    if (this.currentProgressBar) {
      const fill = this.currentProgressBar.querySelector('.progress-bar-fill');
      if (fill) {
        fill.style.transition = 'none';
        fill.style.transform = 'scaleX(0)';
      }
    }

    // Restart manual autoplay if using manual control
    if (this.useManualAutoplay) {
      this.startManualAutoplay();
    } else {
      // Try to restart BlazeSlider's autoplay
      if (this.sliderElement) {
        if (typeof this.sliderElement.restartAutoplay === 'function') {
          this.sliderElement.restartAutoplay();
        } else if (typeof this.sliderElement.pauseAutoplay === 'function' && typeof this.sliderElement.startAutoplay === 'function') {
          this.sliderElement.pauseAutoplay();
          setTimeout(() => {
            this.sliderElement.startAutoplay();
          }, 50);
        }
      }
    }

    // Restart progress bar animation
    setTimeout(() => {
      this.startProgressBar();
    }, 100);
  }

  /**
   * Initialize progress bars
   */
  initializeProgressBars() {
    this.progressBars = Array.from(this.querySelectorAll('.progress-bar-item'));
    
    // Add click handlers to progress bars
    this.progressBars.forEach((bar) => {
      const slideIndex = parseInt(bar.getAttribute('data-slide-index')) || 0;
      
      bar.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (this.sliderElement) {
          // Mark as manual interaction
          this.isManualInteraction = true;
          
          // Get current slide index
          const currentIndex = this.currentSlideIndex || 0;
          const targetIndex = slideIndex;
          
          // If already on target slide, do nothing
          if (currentIndex === targetIndex) {
            return;
          }
          
          // Try multiple BlazeSlider methods to navigate
          let navigated = false;
          
          // Method 1: Try direct navigation methods
          if (typeof this.sliderElement.toSlide === 'function') {
            this.sliderElement.toSlide(targetIndex);
            navigated = true;
          } else if (typeof this.sliderElement.goToSlide === 'function') {
            this.sliderElement.goToSlide(targetIndex);
            navigated = true;
          } else if (typeof this.sliderElement.navigateTo === 'function') {
            this.sliderElement.navigateTo(targetIndex);
            navigated = true;
          }
          // Method 2: Try to access track and move directly
          else {
            const track = this.querySelector('.blaze-track');
            const slides = this.querySelectorAll('.blaze-slide');
            
            if (track && slides[targetIndex]) {
              // Calculate slide width including gap
              const slideWidth = slides[0].offsetWidth || 0;
              const gap = 0; // No gap in banner carousel
              const totalWidth = slideWidth + gap;
              
              // Calculate target position
              const targetPosition = -(targetIndex * totalWidth);
              
              // Move track directly
              track.style.transform = `translateX(${targetPosition}px)`;
              track.style.transition = 'transform 0.3s ease';
              
              // Update current index immediately
              this.currentSlideIndex = targetIndex;
              navigated = true;
              
              // Trigger onSlide callback manually if available
              if (typeof this.sliderElement.onSlide === 'function') {
                // The onSlide callback will be triggered by BlazeSlider's internal mechanism
                // but we can manually update our state
                setTimeout(() => {
                  if (this.sliderElement && typeof this.sliderElement.onSlide === 'function') {
                    // Force update
                    this.currentSlideIndex = targetIndex;
                  }
                }, 50);
              }
            }
            // Method 3: Fallback to next/prev navigation
            else if (typeof this.sliderElement.next === 'function' && typeof this.sliderElement.prev === 'function') {
              const steps = Math.abs(targetIndex - currentIndex);
              const direction = targetIndex > currentIndex ? 'next' : 'prev';
              
              // Navigate step by step with proper timing
              let stepCount = 0;
              const navigateStep = () => {
                if (stepCount < steps) {
                  if (direction === 'next') {
                    this.sliderElement.next();
                  } else {
                    this.sliderElement.prev();
                  }
                  stepCount++;
                  if (stepCount < steps) {
                    setTimeout(navigateStep, 200);
                  } else {
                    navigated = true;
                  }
                }
              };
              
              navigateStep();
            }
          }
          
          // Update current slide index immediately
          this.currentSlideIndex = targetIndex;
          
          // Restart autoplay and progress bar after a short delay
          setTimeout(() => {
            this.restartAutoplayAndProgress();
          }, navigated ? 100 : 300);
        }
      });
    });
  }

  /**
   * Start progress bar animation for current slide
   */
  startProgressBar() {
    // Stop any existing animation
    this.stopProgressBar();

    // Ensure we have a valid slide index
    if (this.currentSlideIndex < 0 || this.currentSlideIndex >= this.progressBars.length) {
      this.currentSlideIndex = 0;
    }

    // Reset all progress bars
    this.progressBars.forEach((bar, index) => {
      bar.classList.remove('active', 'completed');
      const fill = bar.querySelector('.progress-bar-fill');
      if (fill) {
        fill.style.transition = 'none';
        fill.style.transform = 'scaleX(0)';
      }
    });

    // Set active bar
    if (this.progressBars[this.currentSlideIndex]) {
      this.currentProgressBar = this.progressBars[this.currentSlideIndex];
      this.currentProgressBar.classList.add('active');

      const fill = this.currentProgressBar.querySelector('.progress-bar-fill');
      if (fill) {
        // Anima via transform (composited) — sem reflow/repaint por frame
        fill.style.transition = 'none';
        fill.style.transform = 'scaleX(0)';
        this.progressStartTime = performance.now();

        // Use requestAnimationFrame to ensure transition is applied
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            fill.style.transition = `transform ${this.autoplayInterval}ms linear`;
            fill.style.transform = 'scaleX(1)';
          });
        });
      }
    }
  }

  /**
   * Stop progress bar animation
   */
  stopProgressBar() {
    if (this.currentProgressBar) {
      const fill = this.currentProgressBar.querySelector('.progress-bar-fill');
      if (fill) {
        // Fração decorrida por tempo — evita ler layout (getComputedStyle/offsetWidth = reflow forçado)
        const elapsed = performance.now() - (this.progressStartTime || performance.now());
        const frac = Math.max(0, Math.min(elapsed / this.autoplayInterval, 1));
        fill.style.transition = 'none';
        fill.style.transform = `scaleX(${frac})`;
      }
    }
  }

  /**
   * Update progress bar when slide changes
   */
  updateProgressBar() {
    // Mark previous slide as completed
    if (this.currentProgressBar) {
      this.currentProgressBar.classList.remove('active');
      this.currentProgressBar.classList.add('completed');
      const fill = this.currentProgressBar.querySelector('.progress-bar-fill');
      if (fill) {
        fill.style.transition = 'none';
        fill.style.transform = 'scaleX(1)';
      }
    }

    // Start new progress bar for the current slide
    this.startProgressBar();
  }

  /**
   * Cleanup when component is removed
   */
  disconnectedCallback() {
    this.stopManualAutoplay();
  }

  /**
   * Show fallback UI when BlazeSlider fails to load
   */
  showFallbackUI() {
    // Hide navigation controls
    const prevBtn = this.querySelector('.blaze-prev');
    const nextBtn = this.querySelector('.blaze-next');
    const pagination = this.querySelector('.blaze-pagination-banner-carrousel');
    
    if (prevBtn) prevBtn.style.display = 'none';
    if (nextBtn) nextBtn.style.display = 'none';
    if (pagination) pagination.style.display = 'none';

    // Show first slide only
    const slides = this.querySelectorAll('.blaze-slide');
    slides.forEach((slide, index) => {
      if (index > 0) {
        slide.style.display = 'none';
      }
    });

    console.warn('BannerCarrousel: Using fallback display (first slide only)');
  }
}

// Registra o custom element quando o DOM estiver pronto
function defineBannerCarrousel() {
  if (customElements.get('banner-carrousel')) return;
  customElements.define('banner-carrousel', BannerCarrousel);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', defineBannerCarrousel);
} else {
  defineBannerCarrousel();
}
